/**
 * "Ver como cliente" (spec 020, US1): o motivo é obrigatório (10 a 500) e fica
 * registrado com o nome de quem pediu. Com a sessão emitida, a aba nova abre
 * por um LINK — clique direto da pessoa, que nenhum bloqueador de pop-up barra
 * — com o token no fragmento da URL.
 */
import { useState, type FormEvent } from 'react';
import { ExternalLink } from 'lucide-react';
import { IMPERSONATION_REASON_MAX, IMPERSONATION_REASON_MIN, reasonLength } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Label, Textarea } from '@/components/ui/input';
import { Dialog } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import {
  impersonationErrorMessage,
  impersonationTabUrl,
  useStartImpersonation,
} from './impersonation-queries';

export interface ImpersonationTargetRef {
  id: number;
  name: string;
  email: string;
}

export function ImpersonateDialog({
  target,
  onClose,
}: {
  target: ImpersonationTargetRef;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const start = useStartImpersonation();
  const length = reasonLength(reason);
  const valid = length >= IMPERSONATION_REASON_MIN && length <= IMPERSONATION_REASON_MAX;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || start.isPending) return;
    start.mutate({ userId: target.id, reason: reason.trim() });
  };

  return (
    <Dialog open onClose={onClose} title={`Ver o portal como ${target.name}`}>
      {start.data ? (
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Sessão de depuração pronta, só leitura, até{' '}
            <strong className="tabular-nums">{formatDateTime(start.data.session.expiresAt)}</strong>.
          </p>
          <p className="text-[13px] text-muted-foreground">
            A aba nova mostra o portal exatamente como {target.name} vê, com uma faixa no topo. Ela
            fica registrada com o seu nome e o motivo, e pode ser encerrada pela faixa ou em
            Configurações › Sessões de depuração.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Fechar
            </Button>
            <Button asChild size="sm">
              <a
                href={impersonationTabUrl(start.data.token)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => window.setTimeout(onClose, 0)}
              >
                <ExternalLink className="size-4" aria-hidden />
                Abrir o portal como {target.name}
              </a>
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-[13px] text-muted-foreground">
            Abre o portal com a identidade de <strong className="text-foreground">{target.name}</strong>{' '}
            ({target.email}) por até 60 minutos, só leitura: nada é comentado, aberto ou alterado em
            nome da pessoa, e o último acesso dela não muda.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="impersonation-reason">
              Motivo <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="impersonation-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: diz que não vê o ticket 123 no portal"
              className="min-h-20"
              maxLength={IMPERSONATION_REASON_MAX * 2}
              autoFocus
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                De {IMPERSONATION_REASON_MIN} a {IMPERSONATION_REASON_MAX} caracteres. Fica registrado com o
                seu nome.
              </span>
              <span className={length > IMPERSONATION_REASON_MAX ? 'text-destructive tabular-nums' : 'tabular-nums'}>
                {length}/{IMPERSONATION_REASON_MAX}
              </span>
            </div>
          </div>
          {start.error ? (
            <p className="text-sm text-destructive" role="alert">
              {impersonationErrorMessage(start.error)}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" size="sm" disabled={!valid || start.isPending}>
              {start.isPending ? 'Iniciando…' : 'Iniciar sessão'}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
